import { User } from '@prisma/client'
import { Divider, Form, Radio, Switch, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { AppSessionType } from '~/features/appSession/common'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { SelectProviderAction } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAction'
import { OnboardingValues } from '../../common'
import { LabelPicker } from '../components/LabelPicker'
import { LABEL_SUGGESTIONS } from '../objects'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const configuration = {
    appSessionId: appSession?.id,
    appId: appSession?.appId,
  }

  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
  } as OnboardingValues

  const logic = CommonClient.Form.useLogic({
    appSession,
  })

  const hasPermissionCorrupted = (permissions: any[]) => {
    return permissions.some(permission => !!permission?.[0]?.isCorrupted)
  }

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    const { googleCalendarSettings, authorizations } = valuesUpdated
    const permissionCalendarManage = googleCalendarSettings?.permissionManage
    const permissionGmailModify = authorizations?.modify
    const permissionGmailDraft = authorizations?.draft

    const isCorrupted = appSession?.statusOnboarding === 'CORRUPTED'
    const isStillCorrupted = hasPermissionCorrupted([
      permissionCalendarManage,
      permissionGmailModify,
      permissionGmailDraft,
    ])

    if (isCorrupted && !isStillCorrupted) {
      await logic.submitOnboarding()
    }
  }

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        values={valuesSafe}
        onSubmit={handleSubmit}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Abilities
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper label="Draft emails">
          <Form.Item name={['abilities', 'draftEmails']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper label="Label emails">
          <Form.Item name={['abilities', 'labelEmails']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper label="Archive emails">
          <Form.Item name={['abilities', 'archiveEmails']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper label="Manage calendar">
          <Form.Item name={['abilities', 'manageCalendar']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const canLabel = instance.getFieldValue([
              'abilities',
              'labelEmails',
            ])

            if (!canLabel) {
              return <></>
            }

            return (
              <>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, marginTop: 32 }}
                >
                  Labels
                </Typography.Title>

                <Divider className="my-3" />

                <Form.Item
                  name={['labels']}
                  rules={[
                    {
                      required: true,
                      message: 'Please enter at least one label to continue',
                    },
                  ]}
                >
                  <LabelPicker suggestions={LABEL_SUGGESTIONS} />
                </Form.Item>
              </>
            )
          }}
        </Form.Item>

        <Typography.Title level={4} style={{ margin: 0, marginTop: 32 }}>
          Inbox
        </Typography.Title>

        <Divider className="my-3" />

        <Form.Item name={['emailServices', 'provider']}>
          <Radio.Group
            block={true}
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: 'Google',
                value: 'google',
              },
              {
                label: 'Outlook',
                value: 'outlook',
              },
              {
                label: 'IMAP',
                value: 'nylas',
              },
            ]}
          />
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const provider = instance.getFieldValue([
              'emailServices',
              'provider',
            ])

            if (provider === 'nylas') {
              return (
                <CommonClient.Form.InputWrapper label="Add your IMAP Account">
                  <Form.Item
                    name={['emailServices', 'accountNylas']}
                    rules={[
                      {
                        required: true,
                        message: 'Please connect your account to continue',
                      },
                    ]}
                  >
                    <SelectProviderAccount providerKey="nylas" />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>
              )
            }

            if (provider === 'outlook') {
              return (
                <CommonClient.Form.InputWrapper label="Add your Outlook Account">
                  <Form.Item
                    name={['emailServices', 'accountOutlookMail']}
                    rules={[
                      {
                        required: true,
                        message: 'Please connect your account to continue',
                      },
                    ]}
                  >
                    <SelectProviderAccount providerKey="outlookMail" />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>
              )
            }

            return (
              <CommonClient.Form.InputWrapper label="Add your Gmail Account">
                <Form.Item
                  name={['emailServices', 'accountGmail']}
                  rules={[
                    {
                      required: true,
                      message: 'Please connect your account to continue',
                    },
                  ]}
                >
                  <SelectProviderAccount providerKey="gmail" />
                </Form.Item>
              </CommonClient.Form.InputWrapper>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const provider = instance.getFieldValue([
              'emailServices',
              'provider',
            ])

            const providerAccountId = instance.getFieldValue([
              'emailServices',
              'accountGmail',
            ])

            const canDraft = instance.getFieldValue([
              'abilities',
              'draftEmails',
            ])

            if (!providerAccountId || provider !== 'google') {
              return <></>
            }

            return (
              <>
                <Form.Item
                  name={['authorizations', 'modify']}
                  rules={[
                    {
                      validator: (_, value) =>
                        value?.[0]?.isCorrupted
                          ? Promise.reject()
                          : Promise.resolve(),
                      message: 'Authorization is not valid',
                    },
                  ]}
                >
                  <ProviderAccountAuthorization
                    providerAccountId={providerAccountId}
                    scope="https://www.googleapis.com/auth/gmail.modify"
                    ui={{
                      title: 'Manage Your Inbox',
                      iconAntd: 'GoogleOutlined',
                      description: 'Allow me to manage your inbox',
                    }}
                  />
                </Form.Item>
                {canDraft && (
                  <Form.Item
                    name={['authorizations', 'draft']}
                    rules={[
                      {
                        validator: (_, value) =>
                          value?.[0]?.isCorrupted
                            ? Promise.reject()
                            : Promise.resolve(),
                        message: 'Authorization is not valid',
                      },
                    ]}
                  >
                    <ProviderAccountAuthorization
                      providerAccountId={providerAccountId}
                      scope="https://www.googleapis.com/auth/gmail.compose"
                      ui={{
                        title: 'Draft Emails',
                        iconAntd: 'GoogleOutlined',
                        description: 'Allow me to draft emails',
                      }}
                    />
                  </Form.Item>
                )}
              </>
            )
          }}
        </Form.Item>

        <Typography.Title level={4} style={{ margin: 0, marginTop: 32 }}>
          Calendar
        </Typography.Title>

        <Divider className="my-3" />

        <Form.Item name={['calendarServices', 'provider']}>
          <Radio.Group
            block={true}
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: 'Google',
                value: 'google',
              },
              {
                label: 'Outlook',
                value: 'outlook',
              },
            ]}
          />
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const provider = instance.getFieldValue([
              'calendarServices',
              'provider',
            ])

            if (provider === 'google') {
              return (
                <CommonClient.Form.InputWrapper label="Add your Google Account">
                  <Form.Item
                    name={['calendarServices', 'accountGoogleCalendar']}
                  >
                    <SelectProviderAccount providerKey="googleCalendar" />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>
              )
            }

            return (
              <CommonClient.Form.InputWrapper label="Add your Outlook Account">
                <Form.Item
                  name={['calendarServices', 'accountOutlookCalendar']}
                >
                  <SelectProviderAccount providerKey="outlookCalendar" />
                </Form.Item>
              </CommonClient.Form.InputWrapper>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const provider = instance.getFieldValue([
              'calendarServices',
              'provider',
            ])

            const providerAccountId = instance.getFieldValue([
              'calendarServices',
              'accountGoogleCalendar',
            ])

            if (!providerAccountId || provider !== 'google') {
              return <></>
            }

            return (
              <Form.Item
                name={['googleCalendarSettings', 'permissionManage']}
                rules={[
                  {
                    validator: (_, value) =>
                      value?.[0]?.isCorrupted
                        ? Promise.reject()
                        : Promise.resolve(),
                    message: 'Authorization is not valid',
                  },
                ]}
              >
                <ProviderAccountAuthorization
                  providerAccountId={providerAccountId}
                  scope="https://www.googleapis.com/auth/calendar"
                  ui={{
                    title: 'Manage Calendar',
                    iconAntd: 'GoogleOutlined',
                    description: 'Allow me to manage your calendar events',
                  }}
                />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue([
              'calendarServices',
              'provider',
            ])

            const providerAccountId = options.getFieldValue([
              'calendarServices',
              'accountGoogleCalendar',
            ])

            const permission = options.getFieldValue([
              'googleCalendarSettings',
              'permissionManage',
            ])

            if (!permission || !providerAccountId || provider !== 'google') {
              return <></>
            }

            return (
              <Form.Item
                name={['googleCalendarSettings', 'googleCalendarId']}
                label="Pick your calendar"
                rules={[
                  {
                    required: true,
                    message: 'Please choose a calendar to continue',
                  },
                ]}
              >
                <SelectProviderAction
                  providerKey="googleCalendar"
                  providerItemsKey="calendars"
                  providerActionKey="findManyCalendars"
                  providerItemLabel="{item.name}"
                  providerAccountId={providerAccountId}
                  providerAccountScopeValues={permission}
                  providerItemValueKey="id"
                  formValuesConfiguration={configuration}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </CommonClient.Form.Wrapper>
    </>
  )
}
